import {authorize, userInfo, isAnonymous} from '@/services/authorize';
import {login, logout, signUp, resetPassword, confirmReset, socialAuth, impersonate, getUser} from '@/services/accounts';
import {waitFor} from './wait';
import splitter from 'full-name-splitter';
import {userJoinedTeam, userSignedUp} from '@/lib/track/events'

export default {
  state: {
    user: null,
    anonymous: isAnonymous(),
    loginBusy: false,
    loginError: null,
    resetState: null,  // 'busy', 'ok', 'error'
    resetMessage: null,
    userTeams: [],
  },
  mutations: {
    setUser(state, user) {
      state.user = user;
      state.anonymous = isAnonymous();
      state.loginBusy = false;
      state.loginError = null;
      if (user.hasOwnProperty('brandly_team')) {
        state.userTeams = user.brandly_team
      }
    },
    mergeUser(state, partialUser) {
      Object.assign(state.user, partialUser);
    },
    startLoggingIn(state) {
      state.loginBusy = true;
      state.loginError = null;
    },
    errorLoggingIn(state, error) {
      if (error.response && error.response.data) {
        error = error.response.data;
      }

      state.user = null;
      state.loginBusy = false;
      state.loginError = error;
    },
    startReset(state) {
      state.resetState = 'busy';
      state.resetMessage = null;
    },
    resetFailed(state, e) {
      state.resetState = 'error';
      state.resetMessage = e.toString();
    },
    resetSuccess(state, msg) {
      state.resetState = 'ok';
      state.resetMessage = msg;
    },
    removeTeamFromUserTeams(state, teamID) {
      state.userTeams = state.userTeams.filter((t) => t.id !== teamID);
    },
  },
  actions: {
    async authorize({dispatch}) {
      authorize();
      dispatch('setUser', await userInfo());
    },
    setUser({commit, dispatch}, user) {
      commit('setUser', user);
      dispatch('resetDashboardUserState');
    },
    async login({commit, dispatch}, {username, password}) {
      commit('startLoggingIn');
      try{
        let user = await login(username, password);
        await dispatch('setUser', user);
      } catch(e) {
        console.error(e);
        let errText = e;
        try{
          let errDetails = e.response.data;
          if (errDetails.detail) {
            errText = errDetails.detail;
          }
          if (errDetails.non_field_errors) {
            errText = errDetails.non_field_errors.join(', ');
          }
          // fallback
          if (errText === '[object Object]') {
            // Log?
            errText = JSON.stringify(errDetails);
          }
        }catch(e2){
          console.error('Error while unwrapping.', e2);
        }
        commit('errorLoggingIn', errText);
      }
    },
    async logout({dispatch}) {
      logout();
      // Authorize so there is a valid lazy user
      dispatch('authorize');
    },
    async signUp({commit, dispatch}, {username, password, confirm_password, name, first_name, last_name, team, token}) {
      commit('startLoggingIn');
      try {
        if (password !== confirm_password) throw 'Passwords must match';
        if (name) [first_name, last_name] = splitter(name);
        first_name = first_name || '';
        last_name = last_name || '';
        let payload = {email: username, password, first_name, last_name, team, token};
        console.log(payload);
        let user = await signUp(payload);
        await dispatch('setUser', user);
        team ? userJoinedTeam() : userSignedUp();
      } catch(e) {
        commit('errorLoggingIn', e);
      }
    },
    async resetPassword({commit}, email) {
      commit('startReset');
      try{
        commit('resetSuccess', await resetPassword(email));
      } catch(e) {
        if (e.response && e.response.data) e = e.response.data;
        if (e.no_user || (e.email && e.email.no_user)) e = 'No user found for that email';
        if (typeof e === 'object') {
          console.log('Error sending reset email', e);
          e = 'Error sending reset email';
        }
        commit('resetFailed', e.toString())
      }
    },
    async confirmReset({commit, dispatch}, params) {
      commit('startLoggingIn');
      try {
        await confirmReset(params);
        dispatch('setUser', null);
      } catch(e) {
        let errText = e.toString();
        if (e.new_password2) {
          errText = e.new_password2.join(', ');
        } else if (e.token) {
          // expired reset token
          errText = "Password was already reset, please try your password again";
        }
        console.log(e);
        commit('errorLoggingIn', errText);
      }
    },
    async socialAuth({commit, dispatch}, {provider, access_token, code}) {
      commit('startLoggingIn');
      try {
        let user = await socialAuth(provider, {access_token, code});
        dispatch('setUser', user);
        userSignedUp();
      } catch(e) {
        console.error(e);
        let errText = e.toString();
        commit('errorLoggingIn', errText);
      }
    },
    async waitForLogin(context) {
      if (context.getters.isAnonymous) throw 'Not logged in';
      return waitFor('loggedIn', 2000);
    },
    async impersonate({dispatch}, username) {
      let user = await impersonate(username);
      await dispatch('setUser', user);
    },
    async reloadUser({dispatch}) {
      let user = await getUser();
      return dispatch('setUser', user);
    }
  },
  getters: {
    user: state => state.user,
    loginBusy: state => state.loginBusy,
    loginError: state => state.loginError,
    loginErrorText(state) {
      if (!state.loginError || typeof state.loginError === "string") return state.loginError;
      // Assume the object is a list of fields -> errors
      // Just drop the field names and return text.
      return Object.values(state.loginError).join('. ');
    },
    loggedIn: state => !!state.user && !state.user.is_lazy_user,
    resetState: state => state.resetState,
    resetMessage: state => state.resetMessage,
    isAnonymous: state => state.anonymous,
    isAdmin: state => state.user && state.user.user_type == 'admin',
    isStaff: state => state.user && state.user.is_staff,
    userTeams: state => state.userTeams,
  }
}



// WEBPACK FOOTER //
// ./src/store/auth.js